// angular
import {Injectable, effect, inject, signal} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {DecimalPipe} from "@angular/common";
import {TranslateService} from "@ngx-translate/core";
import {MatDialog} from "@angular/material/dialog";
// components
import {CreditCapDialgoComponent} from "@app/+customer/payments/credit-cap-dialgo/credit-cap-dialgo.component";
import {InitialDebtDialogComponent} from "@app/+customer/payments/initial-debt-dialog/initial-debt-dialog.component";
import {PaymentDialogComponent} from "@app/+customer/payments/payment-dialog/payment-dialog.component";
// services
import {RouteService} from "@app/shared/services/route.service";
import {UserService} from "@app/shared/services/user.service";
// types
import {ITableConfig} from "@app/core/components/table/table-config.type";
import {IPayment} from "../interfaces/payment.interface";
import {IWallet} from "../interfaces/wallet.interface";
import {Role} from "@app/shared/types/role.enum";
// utils
import {convertIsoToJalaali} from "@app/shared/utils/convert-iso-to-jalaali";

@Injectable()
export class WalletService {
  private _userRoles: Role[];
  public customerWalletData = signal<IWallet | null>(null);
  public isCustomerWalletDataLoading = signal<boolean>(false);

  // dependencies
  private _httpClient = inject(HttpClient);
  private _routeService = inject(RouteService);
  private _decimal = inject(DecimalPipe);
  private _translate = inject(TranslateService);
  private _dialog = inject(MatDialog);
  private _userService = inject(UserService);
  constructor() {
    effect(() => {
      this._userRoles = this._userService.roles();
    });
  }

  addPayment(newPayment: any) {
    return this._httpClient.post(`payments`, newPayment);
  }
  editPayment(paymentId: string, newPayment: any) {
    return this._httpClient.put(`payments/${paymentId}`, newPayment);
  }
  getPaymentById(paymentId: string) {
    return this._httpClient.get<any>(`payments/${paymentId}`);
  }

  wallets(walletId: string) {
    return this._httpClient.get(`wallets/${walletId}`);
  }
  walletByCustomerId(customerId: string) {
    this.isCustomerWalletDataLoading.set(true);
    this._httpClient.get<any>(`wallets/customer/${customerId}`).subscribe({
      next: (res) => {
        this.customerWalletData.set(res);
        this.isCustomerWalletDataLoading.set(false);
      },
      error: (err) => {
        this.isCustomerWalletDataLoading.set(false);
      },
    });
  }
  updateCreditCap(customerId: string, param: any) {
    return this._httpClient.put<any>(`wallets/customer/${customerId}`, param);
  }
  initialBalance(customerId: string, param: any) {
    return this._httpClient.put<any>(
      `wallets/initial-balance/${customerId}`,
      param
    );
  }

  payments() {
    return this._httpClient.get<any>(
      `payments`.concat(this._routeService.getUrlQueryString())
    );
  }

  public getPaymentsTableConfig(payments: IPayment[]): ITableConfig<IPayment> {
    return {
      name: "payments",
      displayedColumns: [
        "code",
        "amountR",
        "paymentMethod",
        "date",
        "cheque_due_date",
        "description",
        "actions",
      ],
      originalSource: JSON.parse(JSON.stringify(payments)),
      dataSource: payments.map((item) => ({
        ...item,
        amountR: this._decimal.transform(item.amount, "1.0")?.concat(" ریال"),
        paymentMethod: this._translate.instant(
          "enums.PaymentMethodTypeEnum.".concat(item.method)
        ),
        date: convertIsoToJalaali(item.date),
        cheque_due_date: item?.cheque_due_date
          ? convertIsoToJalaali(item?.cheque_due_date)
          : " ",
      })),
      minWidth: "1050px",
      columnsWidths: ["10%", "25%", "20%", "20%", "20%", "50%", "20%"],
      actions: [
        {
          icon: "edit",
          handler: (payment) => this._openEditPaymentDialog(payment.id),
          cssClass: "!text-warning",
          if: () => !!this._userRoles?.includes(Role.CreateCustomerPayment),
        },
      ],
    };
  }

  public openEditCreditCapDialog(customerId: string) {
    const dialogRef = this._dialog.open<CreditCapDialgoComponent>(
      CreditCapDialgoComponent,
      {
        width: "400px",
        maxWidth: "400px",
        data: {customerId},
      }
    );

    dialogRef.afterClosed().subscribe((result) => {
      this.customerWalletData.update((walletData) => ({
        ...walletData,
        ...result,
      }));
    });
  }
  public openInitialBalanceDialog(customerId: string) {
    const dialogRef = this._dialog.open(InitialDebtDialogComponent, {
      width: "400px",
      maxWidth: "400px",
      data: customerId,
    });
    dialogRef.afterClosed().subscribe((result) => {
      this.customerWalletData.update((walletData) => ({
        ...walletData,
        ...result,
      }));
    });
  }

  public openPaymentDialog(customerId: string, payment?: IPayment) {
    const dialogRef = this._dialog.open(PaymentDialogComponent, {
      width: "400px",
      maxWidth: "400px",
      data: {customerId, payment},
    });
    dialogRef.afterClosed().subscribe((result) => {});
  }

  private _openEditPaymentDialog(paymentId: string) {
    this.getPaymentById(paymentId).subscribe((res: IPayment) => {
      this.openPaymentDialog(paymentId, res);
    });
  }
}
